<template>
  <div v-if="visible" class="renew-modal-backdrop" @click.self="$emit('close')">
    <div class="renew-modal">
      <div class="renew-modal__hero">
        <div>
          <span class="renew-modal__eyebrow">Renew subscription</span>
          <h3>Extend this subscription?</h3>
          <p>
            {{
              organizationName
                ? `This will renew the subscription for ${organizationName}.`
                : 'This will renew the selected subscription.'
            }}
          </p>
        </div>

        <button
          type="button"
          class="renew-modal__close"
          aria-label="Close renew modal"
          @click="$emit('close')"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <div class="renew-modal__body">
        <div class="renew-summary-card">
          <span class="renew-summary-card__label">Organization</span>
          <strong>{{ organizationName || 'Not available' }}</strong>
        </div>

        <div class="renew-summary-grid">
          <div class="renew-summary-card">
            <span class="renew-summary-card__label">Current renewal date</span>
            <strong>{{ formattedRenewalDate }}</strong>
          </div>
        </div>

        <label class="renew-form-field" for="renew-subscription-year">
          <span>Select renewal year</span>
          <select id="renew-subscription-year" v-model="selectedYear">
            <option v-for="year in yearOptions" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </label>
      </div>

      <div class="renew-modal__actions">
        <button
          type="button"
          class="modal-button modal-button--ghost"
          :disabled="loading"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          type="button"
          class="modal-button modal-button--primary"
          :disabled="loading"
          @click="handleConfirm"
        >
          {{ loading ? 'Renewing...' : 'Renew Subscription' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RenewSubscriptionModal',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    subscription: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    yearOptions() {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 6 }, (_, index) => String(currentYear + index));
    },

    organizationName() {
      return this.subscription?.organizationName || this.subscription?.organization_name || '';
    },

    formattedRenewalDate() {
      const value =
        this.subscription?.renewalDate ||
        this.subscription?.renewal_date ||
        this.subscription?.endDate ||
        this.subscription?.end_date;

      if (!value) {
        return 'Not available';
      }

      const parsedDate = new Date(value);

      if (Number.isNaN(parsedDate.getTime())) {
        return value;
      }

      return parsedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },

  data() {
    return {
      selectedYear: String(new Date().getFullYear()),
    };
  },

  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.selectedYear = String(new Date().getFullYear());
      }
    },
  },

  methods: {
    handleConfirm() {
      this.$emit('confirm', {
        year: this.selectedYear,
      });
    },
  },
};
</script>

<style scoped>
.renew-modal-backdrop {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1.5rem;
  background: rgba(10, 28, 16, 0.42);
  backdrop-filter: blur(6px);
  z-index: 1200;
}

.renew-modal {
  width: min(100%, 34rem);
  border-radius: 28px;
  border: 1px solid rgba(21, 70, 37, 0.08);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(245, 249, 246, 0.98));
  box-shadow: 0 32px 80px rgba(14, 34, 20, 0.18);
  overflow: hidden;
}

.renew-modal__hero {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1rem;
}

.renew-modal__eyebrow {
  display: inline-flex;
  margin-bottom: 0.45rem;
  color: #2a8a43;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.renew-modal__hero h3 {
  margin: 0 0 0.45rem;
  color: #173425;
}

.renew-modal__hero p {
  margin: 0;
  color: #597161;
  line-height: 1.6;
}

.renew-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border: 0;
  border-radius: 999px;
  background: #edf7ef;
  color: #226c34;
}

.renew-modal__body {
  padding: 0 1.5rem 1rem;
}

.renew-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-top: 0.85rem;
}

.renew-summary-card {
  padding: 1rem 1.05rem;
  border: 1px solid rgba(29, 70, 44, 0.08);
  border-radius: 18px;
  background: rgba(248, 251, 249, 0.96);
}

.renew-summary-card__label {
  display: block;
  margin-bottom: 0.35rem;
  color: #688171;
  font-size: 0.78rem;
}

.renew-summary-card strong {
  color: #173425;
}

.renew-form-field {
  display: grid;
  gap: 0.55rem;
  margin-top: 1rem;
  color: #355343;
  font-weight: 600;
}

.renew-form-field select {
  width: 100%;
  min-height: 3rem;
  padding: 0.8rem 0.95rem;
  border: 1px solid rgba(29, 70, 44, 0.12);
  border-radius: 14px;
  background: #ffffff;
  color: #173425;
  outline: none;
}

.renew-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem 1.5rem;
}

.modal-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 9rem;
  padding: 0.85rem 1.1rem;
  border-radius: 14px;
  font-weight: 700;
  border: 0;
}

.modal-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.modal-button--ghost {
  background: #eef4ef;
  color: #355343;
}

.modal-button--primary {
  background: linear-gradient(135deg, #2f8d49, #215f33);
  color: #ffffff;
}

@media (max-width: 767px) {
  .renew-modal {
    width: 100%;
  }

  .renew-summary-grid {
    grid-template-columns: 1fr;
  }

  .renew-modal__actions {
    flex-direction: column-reverse;
  }

  .modal-button {
    width: 100%;
  }
}
</style>
